import { JSX, useState } from 'react';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    LoadingSpinnerPresets,
    PresetLoadingSpinner,
} from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import {
    Alert,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import {
    CloudDownload,
    DoNotDisturbOn,
    Error,
    LocalActivity,
    PanTool,
    Pending,
    PlayArrow,
    PlayCircle,
    StopCircle,
} from '@mui/icons-material';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { FiltersBox } from '../shared-mui-components/filter/FiltersBox.tsx';
import { useOffstreetTransactionsFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { Outlet, useNavigate, useOutletContext } from 'react-router';
import { makeSearchQueryWithFilters } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import {
    IDENTIFICATION_ICON_SIZE,
    IdentificationBox,
    IdentificationIcon,
    IdentificationIconBox,
    IdentificationType,
} from '../common/components/IdentificationItems.tsx';
import { SvgIcon } from 'dg-web-shared/ui/icons/SvgIcon.tsx';
import { Colors } from 'dg-web-shared/ui/vars.ts';
import {
    BarrierGateIdentificationEntity,
    BarrierGateIdentificationTicket,
} from './OperatorOffstreetTransactionTypes.ts';
import {
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { TranslationForTicketState } from './OperatorOffstreetTransactionTicket.tsx';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import {
    ButtonWithDropdown,
    DropdownItem,
} from '../layout/components/ButtonWithDropdown.tsx';
import {
    ValidFromDisplay,
    ValidFromValidToDisplay,
} from '../common/components/Transaction.tsx';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import { Ticket } from 'dg-web-shared/model/Ticket.ts';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { ExistingOffstreetTransactionsType } from '../common/state/OperatorDataState.ts';
import { useReadOnlyExportContext } from '../app/components/ReadOnlyExportContext.tsx';
import { ConfirmReadOnlyExport } from '../statistics/OperatorCheckinStatisticsRoute.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';

export enum TicketState {
    CREATED = 'CREATED',
    SCANNED = 'SCANNED',
    ALIAS_PENDING = 'ALIAS_PENDING',
    APPROVED = 'APPROVED',
    BOOKED = 'BOOKED',
}

enum EntryWithoutExitState {
    RUNNING = 'RUNNING',
    RUNNING_WITH_ANTIPASSBACK = 'RUNNING_WITH_ANTIPASSBACK',
    COMPLETE = 'COMPLETE',
    CANCELLED = 'CANCELLED',
}

type OffstreetTransactionEntry =
    | PendingTicketTransactionEntry
    | PaidTicketTransactionEntry
    | TicketLostTransactionEntry
    | EntryWithoutExitTransactionEntry
    | ContractTransactionEntry;

interface OffstreetTransactionEntryBase {
    zone: string;
    city: string;
    validFrom: string;
    type: TransactionType;
}

export enum TransactionType {
    PENDING_TICKET = 'PENDING_TICKET',
    PAID_TICKET = 'PAID_TICKET',
    TICKET_LOST = 'TICKET_LOST',
    ENTRY_WITHOUT_EXIT = 'ENTRY_WITHOUT_EXIT',
    CONTRACT = 'CONTRACT',
}

interface PendingTicketTransactionEntry extends OffstreetTransactionEntryBase {
    ticket: Ticket;
    ticketState: TicketState;
    unapprovedAt: string | null;
    type: TransactionType.PENDING_TICKET;
}

interface PaidTicketTransactionEntry extends OffstreetTransactionEntryBase {
    barrierGateIdentificationEntity: BarrierGateIdentificationTicket;
    ticketState: TicketState;
    validTo: string;
    duration: string;
    paymentChannel: PaymentChannel;
    amount: number;
    isRefunded: boolean;
    type: TransactionType.PAID_TICKET;
}

interface TicketLostTransactionEntry extends OffstreetTransactionEntryBase {
    payStationPaymentId: string;
    ticketLostCode: string | null;
    amount: number;
    validTo: string | null;
    paymentChannel: PaymentChannel;
    type: TransactionType.TICKET_LOST;
}

interface EntryWithoutExitTransactionEntry extends OffstreetTransactionEntryBase {
    paymentChannel: PaymentChannel;
    entryWithoutExitId: number;
    entryWithoutExitState: EntryWithoutExitState;
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    ticketState: TicketState | null;
    unapprovedAt: string | null;
    isCancelled: boolean;
    type: TransactionType.ENTRY_WITHOUT_EXIT;
}

interface ContractTransactionEntry extends OffstreetTransactionEntryBase {
    contractExternalId: string;
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    validTo: string;
    duration: string;
    paymentChannel: PaymentChannel;
    amount: number;
    isRefunded: boolean;
    type: TransactionType.CONTRACT;
}

export interface OffstreetTransactionsSearchResult {
    eof: boolean;
    results: OffstreetTransactionEntry[];
}

export function OperatorOffstreetTransactionsRouteLayout() {
    const { operatorData, operatorExternalId } = useOperatorContext();
    const navigate = useNavigate();
    const { language } = useLanguage();

    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        searchText,
        filterConfigState,
    } = useOffstreetTransactionsFilterConfiguration();

    const readOnlyTransactions = useReadOnlyExportContext();
    const [readOnlyExportModalOpen, setReadOnlyExportModalOpen] =
        useState(false);

    const [offstreetTransactionExportState, requestOffstreetTransactionExport] =
        useServerWrite<string, null>(() => ({
            url: `/ui-api/operator-account/offstreet-transactions/${language}/csv-export${makeSearchQueryWithFilters(
                searchText,
                activeFilters,
            )}`,
        }));

    useServerSuccessEffect(
        offstreetTransactionExportState,
        readOnlyTransactions.refetchReadOnlyExportData,
    );
    const hasRunningExport = readOnlyTransactions.runningExportId !== null;
    const notReady = listState.status !== RequestStatus.SUCCESS;

    if (operatorData.existingOffstreetTransactionsType === null) {
        return (
            <PresetLoadingSpinner
                preset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            />
        );
    }

    const divider: DropdownItem = { key: 'divider', divider: true };

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <>
                        <>Offstreet – </>
                        <OffstreetTransactionsTitle
                            offstreetTransactionsType={
                                operatorData.existingOffstreetTransactionsType
                            }
                        />
                    </>
                }
                icon={PlayArrow}
                headerButtons={
                    <Box
                        sx={{
                            marginLeft: 3,
                            marginRight: 3,
                        }}
                    >
                        <ButtonWithDropdown
                            variant="outlined"
                            label={
                                <Localized
                                    de="Exportieren"
                                    fr="Exporter"
                                    it="Esporta"
                                    en="Export"
                                />
                            }
                            startIcon={<CloudDownload />}
                            dropdownItems={[
                                {
                                    key: 'download-list',
                                    label: (
                                        <Localized
                                            de="Liste"
                                            fr="Liste"
                                            it="Lista"
                                            en="List"
                                        />
                                    ),
                                    tooltip: hasRunningExport ? (
                                        <Localized
                                            de="Ein export läuft bereits."
                                            fr="Une exportation est déjà en cours."
                                            it="È già in corso un'esportazione."
                                            en="An export is already running."
                                        />
                                    ) : notReady ? (
                                        <Localized
                                            de="Die Statistik ist noch nicht bereit."
                                            fr="Les statistiques ne sont pas encore prêtes."
                                            it="Le statistiche non sono ancora pronte."
                                            en="The statistics are not ready yet."
                                        />
                                    ) : (
                                        <Localized
                                            de="Die Transaktionen, die den Such-/Filterkriterien entsprechen, werde im CSV-Format exportiert."
                                            fr="Les transactions qui correspondent aux critères de recherche/filtrage sont exportées au format CSV."
                                            it="Le transazioni che corrispondono ai criteri di ricerca/filtro vengono esportate in formato CSV."
                                            en="The transactions that match the search/filter criteria are exported in CSV format."
                                        />
                                    ),
                                    disabled:
                                        offstreetTransactionExportState.status ===
                                            RequestStatus.PENDING ||
                                        hasRunningExport ||
                                        notReady,
                                    onClickCallback: () =>
                                        setReadOnlyExportModalOpen(true),
                                },
                                ...(operatorData.hasCloudConnector
                                    ? [
                                          divider,
                                          {
                                              key: 'download-traffic-report',
                                              label: (
                                                  <Localized
                                                      de="Fahrtenstatistik"
                                                      fr="Rapport de trafic"
                                                      it="Rapporto sul traffico"
                                                      en="Traffic report"
                                                  />
                                              ),
                                              tooltip: (
                                                  <Localized
                                                      de="Die Fahrtenstatistik der letzten 2 Jahre wird im Excel-Format exportiert."
                                                      fr="Le rapport du trafic des 2 dernières années est exporté au format Excel."
                                                      it="La statistica sul traffico degli ultimi 2 anni viene esportata in formato Excel."
                                                      en="The traffic report for the last 2 years is exported in Excel format."
                                                  />
                                              ),
                                              onClickCallback: () => {
                                                  window.open(
                                                      urlWithOperatorExternalId(
                                                          `/ui-api/operator-account/cloud-connector/${language}/traffic-export`,
                                                          operatorExternalId,
                                                      ),
                                                      '_blank',
                                                      'noopener,noreferrer',
                                                  );
                                              },
                                              disabled: false,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </Box>
                }
            />
            <FiltersBox
                searchText={searchText}
                activeFilters={activeFilters}
                filterConfig={filterConfig}
                requestState={listState}
                refetch={refetchList}
                pending={filterConfigState.status === RequestStatus.PENDING}
                tooltip={<OffstreetTransactionsSearchTooltip />}
            />
            <OperatorAsyncLoadedSection
                requestState={listState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                renderError={() => <FetchError refetchList={refetchList} />}
                render={data => {
                    return (
                        <OffstreetTransactionsList
                            data={data}
                            onRowClick={(
                                transactionType: TransactionType,
                                id: string,
                            ) => {
                                navigate(
                                    `${getRouteForTransactionType(transactionType)}/${id}/detail${makeSearchQueryWithFilters(
                                        searchText,
                                        activeFilters,
                                    )}`,
                                );
                            }}
                        />
                    );
                }}
            />
            <OffstreetTransactionsOutlet
                context={{ refetchList: refetchList }}
            />
            <ConfirmReadOnlyExport
                open={readOnlyExportModalOpen}
                onConfirm={() => {
                    requestOffstreetTransactionExport();
                    setReadOnlyExportModalOpen(false);
                }}
                onCancel={() => setReadOnlyExportModalOpen(false)}
            />
        </MainContent>
    );
}

export function useOffstreetTransactionsOutlet() {
    return useOutletContext<OffstreetTransactionsOutletContext>();
}

export function OffstreetTransactionsOutlet({
    context,
}: {
    context: OffstreetTransactionsOutletContext;
}) {
    return <Outlet context={context} />;
}

export interface OffstreetTransactionsOutletContext {
    refetchList: () => void;
}

function getRouteForTransactionType(type: TransactionType): string {
    switch (type) {
        case TransactionType.PENDING_TICKET:
        case TransactionType.PAID_TICKET:
            return 'ticket';
        case TransactionType.TICKET_LOST:
            return 'ticket-lost';
        case TransactionType.CONTRACT:
            return 'contract';
        case TransactionType.ENTRY_WITHOUT_EXIT:
            return 'entry-without-exit';
    }
}

type TransactionRowClickCallback = (
    transactionType: TransactionType,
    id: string,
) => void;

export function OffstreetTransactionsList({
    data,
    onRowClick,
    isParkingaboCustomerView: isParkingaboCustomerView,
}: {
    data: OffstreetTransactionsSearchResult;
    onRowClick: TransactionRowClickCallback | null;
    isParkingaboCustomerView?: boolean;
}) {
    if (data.results.length === 0) {
        return isParkingaboCustomerView ? (
            <Alert severity="info">
                <Localized
                    de="Dieser Benutzer hat keine Transaktionen."
                    fr="Cet utilisateur n'a pas de transactions."
                    it="Questo utente non ha transazioni."
                    en="This user has no transactions."
                />
            </Alert>
        ) : (
            <EmptyResults />
        );
    }

    return (
        <>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCellLayout firstCell>
                                <Localized
                                    de="Anwesenheit"
                                    fr="Présence"
                                    it="Presenza"
                                    en="Presence"
                                />
                            </TableCellLayout>
                            <TableCellLayout>
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            </TableCellLayout>
                            <TableCellLayout>
                                <Localized
                                    de="Parking"
                                    fr="Parking"
                                    it="Parcheggio"
                                    en="Parking"
                                />
                            </TableCellLayout>
                            <TableCellLayout>
                                <Localized
                                    de="Parkzeit"
                                    fr="Heures de stationnement"
                                    it="Orario di sosta"
                                    en="Park time"
                                />
                            </TableCellLayout>
                            {!isParkingaboCustomerView && (
                                <TableCellLayout>
                                    <Localized
                                        de="Kanal"
                                        fr="Canal"
                                        it="Canale"
                                        en="Channel"
                                    />
                                </TableCellLayout>
                            )}
                            <TableCellLayout>
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            </TableCellLayout>
                            <TableCellLayout lastCell>
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            </TableCellLayout>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.results.map(transaction => (
                            <OffstreetTransactionRow
                                key={getKeyForTransaction(transaction)}
                                transaction={transaction}
                                onRowClick={onRowClick}
                                hidePaymentChannel={isParkingaboCustomerView}
                            />
                        ))}
                    </TableBody>
                </Table>
                <ResultLimitAlert
                    eof={data.eof}
                    numberOfResults={data.results.length}
                    hasExport={!isParkingaboCustomerView}
                />
            </TableContainer>
        </>
    );
}

function getKeyForTransaction(transaction: OffstreetTransactionEntry) {
    switch (transaction.type) {
        case TransactionType.PENDING_TICKET:
            return `${transaction.type}-${transaction.ticket.ticketId}`;
        case TransactionType.PAID_TICKET:
            return `${transaction.type}-${transaction.barrierGateIdentificationEntity.ticket.ticketId}`;
        case TransactionType.TICKET_LOST:
            return `${transaction.type}-${transaction.payStationPaymentId}`;
        case TransactionType.ENTRY_WITHOUT_EXIT:
            return `${transaction.type}-${transaction.entryWithoutExitId}`;
        case TransactionType.CONTRACT:
            return `${transaction.type}-${transaction.contractExternalId}`;
    }
}

function OffstreetTransactionRow({
    transaction,
    onRowClick,
    hidePaymentChannel,
}: {
    transaction: OffstreetTransactionEntry;
    onRowClick: TransactionRowClickCallback | null;
    hidePaymentChannel?: boolean;
}): JSX.Element {
    switch (transaction.type) {
        case TransactionType.PENDING_TICKET:
            return (
                <PendingTicketTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                    hidePaymentChannel={hidePaymentChannel}
                />
            );
        case TransactionType.PAID_TICKET:
            return (
                <PaidTicketTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                    hidePaymentChannel={hidePaymentChannel}
                />
            );
        case TransactionType.TICKET_LOST:
            return (
                <TicketLostTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                    hidePaymentChannel={hidePaymentChannel}
                />
            );
        case TransactionType.ENTRY_WITHOUT_EXIT:
            return (
                <EntryWithoutExitTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                    hidePaymentChannel={hidePaymentChannel}
                />
            );
        case TransactionType.CONTRACT:
            return (
                <ContractTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                    hidePaymentChannel={hidePaymentChannel}
                />
            );
    }
}

function PendingTicketTransactionRow({
    transaction,
    onRowClick,
    hidePaymentChannel,
}: {
    transaction: PendingTicketTransactionEntry;
    onRowClick: TransactionRowClickCallback | null;
    hidePaymentChannel?: boolean;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={
                onRowClick
                    ? () =>
                          onRowClick(
                              transaction.type,
                              transaction.ticket.ticketId,
                          )
                    : undefined
            }
        >
            <TableCellLayout firstCell>
                <PresenceWithTooltip
                    icon={<RunningTransactionIcon />}
                    title={<InTheParkingTranslation />}
                />
            </TableCellLayout>
            <TableCellLayout>
                <BarrierGateIdentificationEntityDisplay
                    identificationEntity={{
                        identificationMedia: IdentificationType.TICKET,
                        ticket: transaction.ticket,
                    }}
                />
            </TableCellLayout>
            <TableCellLayout>
                <ParkingDescription transaction={transaction} />
            </TableCellLayout>
            <TableCellLayout>
                <ValidFromDisplay validFrom={transaction.validFrom} />
            </TableCellLayout>
            {!hidePaymentChannel && <TableCellLayout>{EmDash}</TableCellLayout>}
            <TableCellLayout>{EmDash}</TableCellLayout>
            <TableCellLayout lastCell>
                <PendingTicketPaidDisplay
                    ticketState={transaction.ticketState}
                    unapprovedAt={transaction.unapprovedAt}
                />
            </TableCellLayout>
        </TableRow>
    );
}

function PaidTicketTransactionRow({
    transaction,
    onRowClick,
    hidePaymentChannel,
}: {
    transaction: PaidTicketTransactionEntry;
    onRowClick: TransactionRowClickCallback | null;
    hidePaymentChannel?: boolean;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={
                onRowClick
                    ? () =>
                          onRowClick(
                              transaction.type,
                              transaction.barrierGateIdentificationEntity.ticket
                                  .ticketId,
                          )
                    : undefined
            }
        >
            <TableCellLayout firstCell>
                <PresenceWithTooltip
                    icon={<CompletedTransactionIcon />}
                    title={<ExitedTranslation />}
                />
            </TableCellLayout>
            <TableCellLayout>
                <BarrierGateIdentificationEntityDisplay
                    identificationEntity={
                        transaction.barrierGateIdentificationEntity
                    }
                />
            </TableCellLayout>
            <TableCellLayout>
                <ParkingDescription transaction={transaction} />
            </TableCellLayout>
            <TableCellLayout>
                <ValidFromValidToDisplay
                    validFrom={transaction.validFrom}
                    validTo={transaction.validTo}
                />
            </TableCellLayout>
            {!hidePaymentChannel && (
                <TableCellLayout>
                    <PaymentChannelLocalized
                        paymentChannel={transaction.paymentChannel}
                    />
                </TableCellLayout>
            )}
            <TableCellLayout>{transaction.duration}</TableCellLayout>
            <TableCellLayout lastCell>
                {`CHF ${transaction.amount.toFixed(2)} ${transaction.isRefunded ? '(R)' : ''}`}
            </TableCellLayout>
        </TableRow>
    );
}

function TicketLostTransactionRow({
    transaction,
    onRowClick,
    hidePaymentChannel,
}: {
    transaction: TicketLostTransactionEntry;
    onRowClick: TransactionRowClickCallback | null;
    hidePaymentChannel?: boolean;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={
                onRowClick
                    ? () =>
                          onRowClick(
                              transaction.type,
                              transaction.payStationPaymentId,
                          )
                    : undefined
            }
        >
            <TableCellLayout firstCell>
                <PresenceWithTooltip
                    icon={
                        transaction.validTo ? (
                            <CompletedTransactionIcon />
                        ) : (
                            <RunningTransactionIcon />
                        )
                    }
                    title={
                        transaction.validTo ? (
                            <ExitedTranslation />
                        ) : (
                            <InTheParkingTranslation />
                        )
                    }
                />
            </TableCellLayout>
            <TableCellLayout>
                <TicketLostIdentificationIcon
                    ticketLostCode={transaction.ticketLostCode}
                />
            </TableCellLayout>
            <TableCellLayout>
                <ParkingDescription transaction={transaction} />
            </TableCellLayout>
            <TableCellLayout>
                <ValidFromDisplay validFrom={transaction.validFrom} />
            </TableCellLayout>
            {!hidePaymentChannel && (
                <TableCellLayout>
                    <PaymentChannelLocalized
                        paymentChannel={transaction.paymentChannel}
                    />
                </TableCellLayout>
            )}
            <TableCellLayout>{EmDash}</TableCellLayout>
            <TableCellLayout lastCell>
                {`CHF ${transaction.amount.toFixed(2)}`}
            </TableCellLayout>
        </TableRow>
    );
}

function EntryWithoutExitTransactionRow({
    transaction,
    onRowClick,
    hidePaymentChannel,
}: {
    transaction: EntryWithoutExitTransactionEntry;
    onRowClick: TransactionRowClickCallback | null;
    hidePaymentChannel?: boolean;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={
                onRowClick
                    ? () =>
                          onRowClick(
                              transaction.type,
                              transaction.entryWithoutExitId.toString(),
                          )
                    : undefined
            }
        >
            <TableCellLayout firstCell>
                <PresenceWithTooltip
                    icon={
                        <IconForEntryWithoutExitState
                            entryWithoutExitState={
                                transaction.entryWithoutExitState
                            }
                        />
                    }
                    title={
                        <TranslationForEntryWithoutExitState
                            entryWithoutExitState={
                                transaction.entryWithoutExitState
                            }
                        />
                    }
                />
            </TableCellLayout>
            <TableCellLayout>
                <BarrierGateIdentificationEntityDisplay
                    identificationEntity={
                        transaction.barrierGateIdentificationEntity
                    }
                />
            </TableCellLayout>
            <TableCellLayout>
                <ParkingDescription transaction={transaction} />
            </TableCellLayout>
            <TableCellLayout>
                <ValidFromDisplay validFrom={transaction.validFrom} />
            </TableCellLayout>
            {!hidePaymentChannel && (
                <TableCellLayout>
                    <PaymentChannelLocalized
                        paymentChannel={transaction.paymentChannel}
                    />
                </TableCellLayout>
            )}
            <TableCellLayout>{EmDash}</TableCellLayout>
            <TableCellLayout lastCell>
                {transaction.ticketState ? (
                    <PendingTicketPaidDisplay
                        ticketState={transaction.ticketState}
                        unapprovedAt={transaction.unapprovedAt}
                    />
                ) : (
                    EmDash
                )}
            </TableCellLayout>
        </TableRow>
    );
}

function ContractTransactionRow({
    transaction,
    onRowClick,
    hidePaymentChannel,
}: {
    transaction: ContractTransactionEntry;
    onRowClick: TransactionRowClickCallback | null;
    hidePaymentChannel?: boolean;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={
                onRowClick
                    ? () =>
                          onRowClick(
                              transaction.type,
                              transaction.contractExternalId,
                          )
                    : undefined
            }
        >
            <TableCellLayout firstCell>
                <PresenceWithTooltip
                    icon={<CompletedTransactionIcon />}
                    title={<ExitedTranslation />}
                />
            </TableCellLayout>
            <TableCellLayout>
                <BarrierGateIdentificationEntityDisplay
                    identificationEntity={
                        transaction.barrierGateIdentificationEntity
                    }
                />
            </TableCellLayout>
            <TableCellLayout>
                <ParkingDescription transaction={transaction} />
            </TableCellLayout>
            <TableCellLayout>
                <ValidFromValidToDisplay
                    validFrom={transaction.validFrom}
                    validTo={transaction.validTo}
                />
            </TableCellLayout>
            {!hidePaymentChannel && (
                <TableCellLayout>
                    <PaymentChannelLocalized
                        paymentChannel={transaction.paymentChannel}
                    />
                </TableCellLayout>
            )}
            <TableCellLayout>{transaction.duration}</TableCellLayout>
            <TableCellLayout lastCell>
                {`CHF ${transaction.amount.toFixed(2)} ${transaction.isRefunded ? '(R)' : ''}`}
            </TableCellLayout>
        </TableRow>
    );
}

function TranslationForEntryWithoutExitState({
    entryWithoutExitState,
}: {
    entryWithoutExitState: EntryWithoutExitState;
}) {
    switch (entryWithoutExitState) {
        case EntryWithoutExitState.COMPLETE:
            return <ExitedTranslation />;
        case EntryWithoutExitState.RUNNING:
            return <InTheParkingTranslation />;
        case EntryWithoutExitState.RUNNING_WITH_ANTIPASSBACK:
            return (
                <Localized
                    de="Im Parking (Antipassback aktiv)"
                    fr="Dans le parking (anti-passback actif)"
                    it="Nel parcheggio (anti-passback attivo)"
                    en="In the parking (anti-passback active)"
                />
            );
        case EntryWithoutExitState.CANCELLED:
            return (
                <Localized
                    de="Annulliert"
                    fr="Annulée"
                    it="Annullata"
                    en="Cancelled"
                />
            );
    }
}

function InTheParkingTranslation() {
    return (
        <Localized
            de="Im Parking"
            fr="Sur le parking"
            it="Nel parcheggio"
            en="In the parking"
        />
    );
}

function ExitedTranslation() {
    return <Localized de="Ausgefahren" fr="Quitté" it="Uscito" en="Exited" />;
}

function PendingTicketPaidDisplay({
    ticketState,
    unapprovedAt,
}: {
    ticketState: TicketState;
    unapprovedAt: string | null;
}): JSX.Element {
    switch (ticketState) {
        case TicketState.APPROVED:
            return (
                <PresenceWithTooltip
                    icon={
                        <Pending
                            style={{ color: Colors.green, fontSize: 'inherit' }}
                        />
                    }
                    title={
                        <TranslationApprovedTicketWithUnapprovedAt
                            unapprovedAt={unapprovedAt}
                        />
                    }
                />
            );
        case TicketState.ALIAS_PENDING:
            return (
                <PresenceWithTooltip
                    icon={
                        <Error
                            style={{
                                color: Colors.yellow,
                                fontSize: 'inherit',
                            }}
                        />
                    }
                    title={<TranslationForTicketState state={ticketState} />}
                />
            );
        default:
            return <>{EmDash}</>;
    }
}

export function TranslationApprovedTicketWithUnapprovedAt({
    unapprovedAt,
}: {
    unapprovedAt: string | null;
}) {
    const unapprovedAtFormatted = unapprovedAt
        ? Formatter.dayMonthYearHourMinute(DateTime.fromISO(unapprovedAt))
        : '';
    return (
        <Localized
            de={`Freigegeben (Ablauf: ${unapprovedAtFormatted})`}
            fr={`Approuvé (expiration: ${unapprovedAtFormatted})`}
            it={`Approvato (scadenza: ${unapprovedAtFormatted})`}
            en={`Approved (expiry: ${unapprovedAtFormatted})`}
        />
    );
}

function IconForEntryWithoutExitState({
    entryWithoutExitState,
}: {
    entryWithoutExitState: EntryWithoutExitState;
}): JSX.Element {
    switch (entryWithoutExitState) {
        case EntryWithoutExitState.RUNNING:
            return <RunningTransactionIcon />;
        case EntryWithoutExitState.RUNNING_WITH_ANTIPASSBACK:
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RunningTransactionIcon />
                    <PanTool
                        sx={{
                            color: Colors.yellow,
                            fontSize: '24px',
                            margin: '2px',
                        }}
                    />
                </Box>
            );
        case EntryWithoutExitState.COMPLETE:
            return <CompletedTransactionIcon />;
        case EntryWithoutExitState.CANCELLED:
            return <CancelledTransactionIcon />;
    }
}

function CompletedTransactionIcon() {
    return <StopCircle style={{ color: Colors.grey, fontSize: 'inherit' }} />;
}

function CancelledTransactionIcon() {
    return (
        <DoNotDisturbOn style={{ color: Colors.grey, fontSize: 'inherit' }} />
    );
}

function RunningTransactionIcon() {
    return <PlayCircle sx={{ color: Colors.green, fontSize: 'inherit' }} />;
}

function BarrierGateIdentificationEntityDisplay({
    identificationEntity,
}: {
    identificationEntity: BarrierGateIdentificationEntity;
}): JSX.Element {
    switch (identificationEntity.identificationMedia) {
        case IdentificationType.BADGE:
            return (
                <IdentificationBox>
                    <IdentificationIcon
                        identificationType={IdentificationType.BADGE}
                        licensePlateType={null}
                    />
                    <BarrierGateIdentificationString
                        identificationType={IdentificationType.BADGE}
                        identificationString={identificationEntity.labelNr}
                    />
                </IdentificationBox>
            );
        case IdentificationType.LICENSE_PLATE:
            return (
                <IdentificationBox>
                    <IdentificationIcon
                        identificationType={IdentificationType.LICENSE_PLATE}
                        licensePlateType={identificationEntity.licenseGroup}
                    />
                    <BarrierGateIdentificationString
                        identificationType={IdentificationType.LICENSE_PLATE}
                        identificationString={`${identificationEntity.licenseNumber} ${identificationEntity.countryCode}`}
                    />
                </IdentificationBox>
            );
        case IdentificationType.QR_CODE:
            return (
                <IdentificationBox>
                    <IdentificationIcon
                        identificationType={IdentificationType.QR_CODE}
                        licensePlateType={null}
                    />
                    <BarrierGateIdentificationString
                        identificationType={IdentificationType.LICENSE_PLATE}
                        identificationString={identificationEntity.qrCodeId}
                    />
                </IdentificationBox>
            );
        case IdentificationType.TICKET:
            return (
                <IdentificationBox>
                    <IdentificationIcon
                        identificationType={IdentificationType.TICKET}
                        licensePlateType={null}
                    />
                    <BarrierGateIdentificationString
                        identificationType={IdentificationType.TICKET}
                        identificationString={
                            identificationEntity.ticket.ticketDisplay
                        }
                    />
                </IdentificationBox>
            );
    }
}

export function TicketLostIdentificationIcon({
    ticketLostCode,
}: {
    ticketLostCode: string | null;
}) {
    return (
        <IdentificationBox>
            <IdentificationIconBox>
                <SvgIcon
                    icon={<LocalActivity />}
                    width={IDENTIFICATION_ICON_SIZE}
                    height={IDENTIFICATION_ICON_SIZE}
                />
            </IdentificationIconBox>
            {ticketLostCode && <Box>{ticketLostCode}</Box>}
        </IdentificationBox>
    );
}

export function BarrierGateIdentificationString({
    identificationType,
    identificationString,
    sx,
}: {
    identificationType: IdentificationType;
    identificationString: string;
    sx?: SxProps<Theme>;
}): JSX.Element {
    switch (identificationType) {
        case IdentificationType.TICKET:
        case IdentificationType.QR_CODE:
        case IdentificationType.LICENSE_PLATE:
            return <Box sx={sx}>{identificationString}</Box>;
        case IdentificationType.BADGE:
            return (
                <Box sx={sx}>
                    {addSpacesEveryFourthFromEnd(identificationString)}
                </Box>
            );
    }
}

export function addSpacesEveryFourthFromEnd(input: string): string {
    return input.replace(/(.)(?=(.{4})+$)/g, '$1 ');
}

function ParkingDescription({
    transaction,
}: {
    transaction: OffstreetTransactionEntry;
}): JSX.Element {
    return (
        <>
            {transaction.zone} {EmDash} {transaction.city}
        </>
    );
}

function PresenceWithTooltip({
    title,
    icon,
}: {
    title: JSX.Element;
    icon: JSX.Element;
}) {
    return (
        <Tooltip title={title}>
            <Box sx={{ display: 'flex' }}>
                {' '}
                {/* needed for the tooltip to work... */}
                <Box sx={{ fontSize: '28px', display: 'flex' }}>{icon}</Box>
            </Box>
        </Tooltip>
    );
}

export function OffstreetTransactionsTitle({
    offstreetTransactionsType,
}: {
    offstreetTransactionsType: ExistingOffstreetTransactionsType;
}): JSX.Element {
    switch (offstreetTransactionsType) {
        case ExistingOffstreetTransactionsType.TICKET:
            return (
                <Localized
                    de="Tickets"
                    fr="Tickets"
                    it="Tickets"
                    en="Tickets"
                />
            );
        case ExistingOffstreetTransactionsType.OFFSTREET_TRANSACTIONS:
            return (
                <Localized
                    de="Parkvorgänge"
                    fr="Procédures"
                    it="Procedure"
                    en="Procedures"
                />
            );
        case ExistingOffstreetTransactionsType.TICKET_AND_OFFSTREET_TRANSACTIONS:
            return (
                <Localized
                    de="Parkvorgänge & Tickets"
                    fr="Procédures & Tickets"
                    it="Procedure & Tickets"
                    en="Procedures & Tickets"
                />
            );
    }
}

function TableCellLayout({
    children,
    firstCell,
    lastCell,
}: {
    children: JSX.Element | string;
    firstCell?: boolean;
    lastCell?: boolean;
}) {
    return (
        <TableCell
            sx={{
                paddingLeft: firstCell ? '24px' : '12px',
                paddingRight: lastCell ? '24px' : '12px',
            }}
        >
            {children}
        </TableCell>
    );
}

function OffstreetTransactionsSearchTooltip() {
    return (
        <Box
            sx={{
                fontSize: 14,
                padding: '18px 18px 5px 18px',
                width: '300px',
            }}
        >
            <Localized
                de={
                    <>
                        Verfügbare Suchkriterien:
                        <ul>
                            <li>Ticketnummer</li>
                            <li>Badgenummer</li>
                            <li>Kennzeichen</li>
                            <li>QR-Code Nummer</li>
                            <li>Transaktion-ID</li>
                        </ul>
                    </>
                }
                fr={
                    <>
                        Critères de recherche disponibles:
                        <ul>
                            <li>numéro de ticket</li>
                            <li>numéro de badge</li>
                            <li>numéro de plaque</li>
                            <li>numéro de code QR</li>
                            <li>ID transaction</li>
                        </ul>
                    </>
                }
                it={
                    <>
                        Criteri di ricerca disponibili:
                        <ul>
                            <li>numero del ticket</li>
                            <li>numero del badge</li>
                            <li>numero di targa</li>
                            <li>numero del codice QR</li>
                            <li>ID transazione</li>
                        </ul>
                    </>
                }
                en={
                    <>
                        Available search criteria:
                        <ul>
                            <li>ticket number</li>
                            <li>badge number</li>
                            <li>number plate number</li>
                            <li>QR code number</li>
                            <li>transaction ID</li>
                        </ul>
                    </>
                }
            />
        </Box>
    );
}
